import { createContext, useContext, useState, useEffect } from 'react';
import supabase from '../config/supabaseClient';

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailChangeInProgress, setEmailChangeInProgress] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for changes on auth state (sign in, sign out, etc.)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null);

      // Handle email change events
      if (event === 'EMAIL_CHANGE_REQUEST') {
        setEmailChangeInProgress(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signUp = async (email, password, options = {}) => {
    try {
    
      const redirectUrl = `${window.location.origin}/auth/callback`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: options.data,
          emailRedirectTo: redirectUrl
        }
      });


      if (error) {
        console.error('Signup error:', error);
        if (error.message?.toLowerCase().includes('user already registered')) {
          return {
            data: null,
            error: {
              message: 'This email is already registered. Please sign in instead.',
              status: 400
            }
          };
        }

        return { 
          data: null, 
          error: {
            message: error.message || 'Failed to create account. Please try again.',
            status: error.status || 500
          }
        };
      }

      // Account created successfully
      if (data?.user) {
        return { data, error: null };
      } else {
        console.error('No user data received from signup');
        return {
          data: null,
          error: {
            message: 'Failed to create account. Please try again.',
            status: 500
          }
        };
      }
    } catch (error) {
      console.error('Sign up error:', error);
      return {
        data: null,
        error: {
          message: 'An unexpected error occurred. Please try again.',
          status: 500
        }
      };
    }
  };

  const signIn = async (email, password, options = {}) => {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: {
          persistSession: options.options?.persistSession ?? true
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  };

  const value = {
    signUp,
    signIn,
    signOut: () => supabase.auth.signOut(),
    
    // Simple password reset implementation
    resetPassword: async (email) => {
      try {
        // Get the base URL (production or local)
        const baseUrl = process.env.REACT_APP_SITE_URL || window.location.origin;
        // Set the reset URL - using the same URL for both redirects
        const resetUrl = `${baseUrl}/update-password`;
      
      
        const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: resetUrl,
        });

        if (error) {
          console.error('Password reset error:', error);
          throw error;
        }

        return { data, error: null };
      } catch (error) {
        console.error('Error in resetPassword:', error);
        return {
          data: null,
          error: {
            message: error.message || 'Failed to send password reset email. Please try again.',
            status: error.status || 500
          }
        };
      }
    },

    // Simple password update implementation
    updatePassword: async (newPassword) => {
      try {
        
        const { data, error } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (error) {
          console.error('Password update error:', error);
          throw error;
        }

        return { data, error: null };
      } catch (error) {
        console.error('Error in updatePassword:', error);
        return {
          data: null,
          error: {
            message: error.message || 'Failed to update password. Please try again.',
            status: error.status || 500
          }
        };
      }
    },
    
    user,
    emailChangeInProgress
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};